import type { ImportedBrepRef } from '../loaders/types'
import {
  isImportedBrepSidecarEntryPayloadVerified,
  type ImportedBrepSidecarDocument,
  type ImportedBrepSidecarEntry,
  type ImportedTopoSidecarDocument,
} from '../sidecar'

/**
 * 导入 sidecar 文档缓存统计。
 */
export interface ImportedBrepRuntimeCacheStats {
  sidecarDocumentCount: number
  sidecarEntryCount: number
  sidecarRequestCount: number
  topoSidecarDocumentCount: number
  topoSidecarRequestCount: number
  meshDataCount: number
  meshRequestCount: number
}

/**
 * 导入拓扑运行时缓存。
 *
 * 设计说明：
 * 1. topo sidecar 文档、BRep sidecar 文档与 topo MeshData 都属于导入链路的权威运行时缓存，应放在 cad-core；
 * 2. web-client 只决定缓存实例的生命周期，不再持有缓存实现细节；
 * 3. 通过 in-flight promise 去重，可以避免同一装配递归加载时重复下载与重复解析。
 */
export interface ImportedBrepRuntimeCache {
  /**
   * 获取已缓存的 sidecar 文档。
   *
   * @param sidecarFileId 归一化后的 sidecar 文件 ID。
   * @returns 已缓存文档。
   */
  getSidecarDocument: (sidecarFileId: string) => ImportedBrepSidecarDocument | undefined
  /**
   * 按 BRep 引用读取已缓存的 sidecar entry。
   *
   * 设计说明：
   * 导出阶段使用加载时已经水合过的 BRep payload，不能再根据原始文件路径回读 sidecar。
   *
   * @param brepRef BRep 引用。
   * @returns 已缓存 entry；未命中时返回 undefined。
   */
  getSidecarEntry: (brepRef: ImportedBrepRef) => ImportedBrepSidecarEntry | undefined
  /**
   * 获取或创建 sidecar 文档加载任务。
   *
   * @param sidecarFileId sidecar 文件 ID。
   * @param loader 真正的加载函数。
   * @returns sidecar 文档。
   */
  getOrCreateSidecarDocument: (
    sidecarFileId: string,
    loader: () => Promise<ImportedBrepSidecarDocument>,
  ) => Promise<ImportedBrepSidecarDocument>
  /**
   * 获取已缓存的 topo sidecar 文档。
   *
   * @param sidecarFileId 归一化后的 topo sidecar 文件 ID。
   * @returns 已缓存文档。
   */
  getTopoSidecarDocument: (sidecarFileId: string) => ImportedTopoSidecarDocument | undefined
  /**
   * 获取或创建 topo sidecar 文档加载任务。
   *
   * @param sidecarFileId topo sidecar 文件 ID。
   * @param loader 真正的加载函数。
   * @returns topo sidecar 文档。
   */
  getOrCreateTopoSidecarDocument: (
    sidecarFileId: string,
    loader: () => Promise<ImportedTopoSidecarDocument>,
  ) => Promise<ImportedTopoSidecarDocument>
  /**
   * 获取已缓存的 topo MeshData。
   *
   * @param sha256 topo MeshData 缓存键。
   * @returns 已缓存的 topo 网格。
   */
  getMeshData: (sha256: string) => MeshData | undefined
  /**
   * 获取或创建 topo MeshData 解析任务。
   *
   * @param sha256 topo MeshData 缓存键。
   * @param loader 真正的 topo MeshData 解析函数。
   * @returns MeshData。
   */
  getOrCreateMeshData: (
    sha256: string,
    loader: () => Promise<MeshData>,
  ) => Promise<MeshData>
  /**
   * 获取缓存统计。
   *
   * @returns 统计快照。
   */
  stats: () => ImportedBrepRuntimeCacheStats
  /**
   * 清空缓存。
   */
  clear: () => void
}

/**
 * 创建导入 BRep 运行时缓存。
 *
 * @returns 缓存实例。
 */
export function createImportedBrepRuntimeCache(): ImportedBrepRuntimeCache {
  const sidecarDocumentByFileId = new Map<string, ImportedBrepSidecarDocument>()
  const sidecarEntryByRefKey = new Map<string, ImportedBrepSidecarEntry>()
  const sidecarEntryBySha256 = new Map<string, ImportedBrepSidecarEntry>()
  const sidecarRequestByFileId = new Map<string, Promise<ImportedBrepSidecarDocument>>()
  const topoSidecarDocumentByFileId = new Map<string, ImportedTopoSidecarDocument>()
  const topoSidecarRequestByFileId = new Map<string, Promise<ImportedTopoSidecarDocument>>()
  const meshDataBySha256 = new Map<string, MeshData>()
  const meshRequestBySha256 = new Map<string, Promise<MeshData>>()

  /**
   * 生成 sidecar entry 索引键。
   *
   * @param brepRef BRep 引用。
   * @returns 运行时缓存键。
   */
  function buildSidecarEntryRefKey(brepRef: ImportedBrepRef): string {
    return `${brepRef.key}\n${brepRef.sha256}\n${brepRef.encoding}`
  }

  /**
   * 将 sidecar 文档中的 payload 建立运行时索引。
   *
   * 设计说明：
   * 1. 导出阶段不能再依赖原始 sidecar 文件路径，内联 part 的 fileId 是运行时伪 ID；
   * 2. BRep payload 的稳定身份是 sha256 + key + encoding，因此在加载成功时统一缓存；
   * 3. 只保存纯文本 payload，不绑定项目文件路径，避免编辑后的装配语义被导入路径反向污染。
   *
   * @param document sidecar 文档。
   */
  function indexSidecarDocumentEntries(document: ImportedBrepSidecarDocument): void {
    Object.entries(document.entries).forEach(([key, entry]) => {
      sidecarEntryByRefKey.set(`${key}\n${entry.sha256}\n${entry.encoding}`, entry)
      sidecarEntryBySha256.set(entry.sha256, entry)
    })
  }

  return {
    getSidecarDocument: (sidecarFileId) => sidecarDocumentByFileId.get(sidecarFileId),
    getSidecarEntry: (brepRef) => {
      const candidate = sidecarEntryByRefKey.get(buildSidecarEntryRefKey(brepRef))
        ?? sidecarEntryBySha256.get(brepRef.sha256)
      return isImportedBrepSidecarEntryPayloadVerified(candidate) ? candidate : undefined
    },
    getOrCreateSidecarDocument: async (sidecarFileId, loader) => {
      const cachedDocument = sidecarDocumentByFileId.get(sidecarFileId)
      if (cachedDocument) {
        indexSidecarDocumentEntries(cachedDocument)
        return cachedDocument
      }

      const pendingRequest = sidecarRequestByFileId.get(sidecarFileId)
      if (pendingRequest) {
        return pendingRequest
      }

      const nextRequest = loader()
        .then((document) => {
          sidecarDocumentByFileId.set(sidecarFileId, document)
          indexSidecarDocumentEntries(document)
          sidecarRequestByFileId.delete(sidecarFileId)
          return document
        })
        .catch((error) => {
          sidecarRequestByFileId.delete(sidecarFileId)
          throw error
        })

      sidecarRequestByFileId.set(sidecarFileId, nextRequest)
      return nextRequest
    },
    getTopoSidecarDocument: (sidecarFileId) => topoSidecarDocumentByFileId.get(sidecarFileId),
    getOrCreateTopoSidecarDocument: async (sidecarFileId, loader) => {
      const cachedDocument = topoSidecarDocumentByFileId.get(sidecarFileId)
      if (cachedDocument) {
        return cachedDocument
      }

      const pendingRequest = topoSidecarRequestByFileId.get(sidecarFileId)
      if (pendingRequest) {
        return pendingRequest
      }

      const nextRequest = loader()
        .then((document) => {
          topoSidecarDocumentByFileId.set(sidecarFileId, document)
          topoSidecarRequestByFileId.delete(sidecarFileId)
          return document
        })
        .catch((error) => {
          topoSidecarRequestByFileId.delete(sidecarFileId)
          throw error
        })

      topoSidecarRequestByFileId.set(sidecarFileId, nextRequest)
      return nextRequest
    },
    getMeshData: (sha256) => meshDataBySha256.get(sha256),
    getOrCreateMeshData: async (sha256, loader) => {
      const cachedMeshData = meshDataBySha256.get(sha256)
      if (cachedMeshData) {
        return cachedMeshData
      }

      const pendingRequest = meshRequestBySha256.get(sha256)
      if (pendingRequest) {
        return pendingRequest
      }

      const nextRequest = loader()
        .then((meshData) => {
          meshDataBySha256.set(sha256, meshData)
          meshRequestBySha256.delete(sha256)
          return meshData
        })
        .catch((error) => {
          meshRequestBySha256.delete(sha256)
          throw error
        })

      meshRequestBySha256.set(sha256, nextRequest)
      return nextRequest
    },
    stats: () => ({
      sidecarDocumentCount: sidecarDocumentByFileId.size,
      sidecarEntryCount: Array.from(sidecarEntryBySha256.values())
        .filter(isImportedBrepSidecarEntryPayloadVerified)
        .length,
      sidecarRequestCount: sidecarRequestByFileId.size,
      topoSidecarDocumentCount: topoSidecarDocumentByFileId.size,
      topoSidecarRequestCount: topoSidecarRequestByFileId.size,
      meshDataCount: meshDataBySha256.size,
      meshRequestCount: meshRequestBySha256.size,
    }),
    clear: () => {
      sidecarDocumentByFileId.clear()
      sidecarEntryByRefKey.clear()
      sidecarEntryBySha256.clear()
      sidecarRequestByFileId.clear()
      topoSidecarDocumentByFileId.clear()
      topoSidecarRequestByFileId.clear()
      meshDataBySha256.clear()
      meshRequestBySha256.clear()
    },
  }
}
